import {
    getSourceLayoutPointer,
    getStructurePointer,
    getVirtualStructurePointer
} from '../../conversionDal/utils/pointer'
import type {ComponentStageData, ConversionDal, VirtualStructureEntryItem, ReadOnlyExtensionAPI} from '../../types'
import {
    generateChildLayout,
    generateContainerLayout,
    generatePinnedChildLayout,
    shapeRules,
    sortHeaderContainerChildren
} from './common'
import type {StructureEntryItem} from '../../conversionDal/types'
import {isHeaderContainer, isPinned, updateTargetLayout} from '../common'
import type {Pointer} from '@wix/document-services-types'
import {VIRTUAL_TAG} from '../grouping/constants'

const processRegularContainerChildren = (
    children: string[],
    conversionDal: ConversionDal,
    containerPointer: Pointer,
    isContainerInsideHeader: boolean
) => {
    let startRow = 0
    for (const [, childId] of children.entries()) {
        const childPointer = getStructurePointer({id: childId})
        if (isPinned({conversionDal, componentPointer: childPointer})) {
            updateTargetLayout({conversionDal, componentPointer: childPointer}, generatePinnedChildLayout())
        } else {
            updateTargetLayout({conversionDal, componentPointer: childPointer}, generateChildLayout(startRow + 1))
            if (!isContainerInsideHeader) startRow++
        }
    }
    const {containerLayout} = conversionDal.get(getSourceLayoutPointer(containerPointer)) ?? {}
    updateTargetLayout(
        {conversionDal, componentPointer: containerPointer},
        {
            containerLayout: {
                ...containerLayout,
                ...generateContainerLayout(startRow + 1)
            }
        }
    )
}

const getRealChildrenAfterOrder = (
    conversionDal: ConversionDal,
    virtualStructurePointer: Pointer,
    realChildren: string[] = []
): string[] => {
    const virtualStructure = conversionDal.get(virtualStructurePointer)

    if (virtualStructure) {
        for (const childId of virtualStructure.components) {
            const virtualChildPointer = getVirtualStructurePointer({id: childId})
            const child = conversionDal.get(virtualChildPointer)

            if (child.componentType === VIRTUAL_TAG) {
                getRealChildrenAfterOrder(conversionDal, virtualChildPointer, realChildren)
            } else {
                realChildren.push(childId)
            }
        }
    }

    return realChildren
}

export const createOrderVirtualStructure = (readOnlyExtensionAPI: ReadOnlyExtensionAPI) => {
    return ({componentPointer, conversionDal, config}: ComponentStageData) => {
        conversionDal.modify(componentPointer, value => ({
            ...value,
            components: [...value.components].sort((childIdA, childIdB) => {
                return shapeRules[config.requiredOrder](
                    (conversionDal.get(getVirtualStructurePointer({id: childIdA})) as VirtualStructureEntryItem)
                        .measurements,
                    (conversionDal.get(getVirtualStructurePointer({id: childIdB})) as VirtualStructureEntryItem)
                        .measurements,
                    readOnlyExtensionAPI.isRTL()
                )
            })
        }))
        return {appliedHeuristics: [config.requiredOrder]}
    }
}

export const orderRealStructure = ({componentPointer, conversionDal}: ComponentStageData) => {
    const component = conversionDal.get(componentPointer)
    if (!component?.components || component.type !== 'Container') {
        return
    }

    const virtualStructurePointer = getVirtualStructurePointer(componentPointer)
    const virtualStructure = conversionDal.get(virtualStructurePointer)

    const children: string[] = virtualStructure
        ? getRealChildrenAfterOrder(conversionDal, virtualStructurePointer)
        : conversionDal.get(componentPointer).components

    const isContainerInsideHeader = isHeaderContainer({conversionDal, componentPointer})
    processRegularContainerChildren(children, conversionDal, componentPointer, isContainerInsideHeader)

    const components = isContainerInsideHeader
        ? [...children].sort(sortHeaderContainerChildren(conversionDal))
        : children

    conversionDal.modify(componentPointer, (value: StructureEntryItem) => ({
        ...value,
        components
    }))
}
